import React from "react";
import { Typography, Button, Box, Stack } from "@mui/material";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ScheduleComponent, Day, Week, WorkWeek, Month, Agenda, Inject, ResourcesDirective, ResourceDirective } from "@syncfusion/ej2-react-schedule";
import { AuthenticatedTemplate } from "@azure/msal-react";
import { useGetRoomsQuery, useGetPhysiciansQuery, useGetPayersQuery, useGetServicesQuery, useGetProceduresQuery } from "../../../services/ConfigurationService";
import SectionPaper from "../../common/components/styled-ui-elements/SectionPaper";
import { useGetScheduleItemsQuery } from "../../../services/EncounterService";
import { PageLoading as Loading, CenterSelector } from "../../common/components";
import { useCenterSelection } from "../../common/hooks/useCenterSelection";
import SearchPatientsButton from '../components/commands/SearchPatientsButton';
import { ScheduleExportButton } from '../components/dialogs';

const ScheduleContent: React.FC = () => {
    const [searchParams] = useSearchParams();
    const company = searchParams.get("company");
    const center = searchParams.get("center");
    const centerId = `${company}-${center}`;
    const { handleCenterChange } = useCenterSelection();
    
    // Don't proceed if company is missing
    if (!company) {
        return (
            <SectionPaper sx={{ p: 4, mt: 4 }}>
                <Typography color="error">
                    Missing required URL parameter: company
                </Typography>
                <Typography variant="body2" sx={{ mt: 2 }}>
                    Expected URL format: /schedule?company=COMPANY&center=CENTER
                </Typography>
            </SectionPaper>
        );
    }

    if (!center) {
        return (
            <SectionPaper sx={{ p: 4, mt: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Typography variant="h6">Select a center:</Typography>
                    <CenterSelector value="" onChange={handleCenterChange} />
                </Box>
            </SectionPaper>
        );
    }
    
    // Calculate date range for the schedule query (2 months prior and all future)
    const today = new Date();
    const startDate = new Date(today);
    startDate.setMonth(today.getMonth() - 2); // 2 months before today
    startDate.setDate(1); // Start from the 1st of that month for consistency
    
    const endDate = new Date(today);
    endDate.setFullYear(today.getFullYear() + 2); // 2 years in the future to capture "all future" schedules
    endDate.setMonth(11); // December
    endDate.setDate(31); // End of year
    
    const { data: rooms, isLoading, isError } = useGetRoomsQuery({ centerId });
    const { data: physicians, isLoading: isLoadingPhysicians, isError: isErrorPhysicians } = useGetPhysiciansQuery({ company: company!, center: centerId });
    const { data: payers, isLoading: isLoadingPayers, isError: isErrorPayers } = useGetPayersQuery({ company: company!, centerId });
    const { data: services, isLoading: isLoadingServices, isError: isErrorServices } = useGetServicesQuery({ centerId });
    const { data: procedures, isLoading: isLoadingProcedures, isError: isErrorProcedures } = useGetProceduresQuery({ centerId });
    
    // Filter out inactive rooms (treat undefined as false/active)
    const activeRooms = React.useMemo(() => 
        (rooms || []).filter(room => !room.isInactive), [rooms]);
    
    // Get schedule data from the new API endpoint
    const { data: scheduleItems, isLoading: isLoadingSchedule, isError: isErrorSchedule, error: scheduleError } = useGetScheduleItemsQuery({
        centerId: center!,
        startDate: startDate.toISOString().split('T')[0], // Format as YYYY-MM-DD
        endDate: endDate.toISOString().split('T')[0], // Format as YYYY-MM-DD
    });

    // Preprocess physicians to add fullName property
    const processedPhysicians = Array.isArray(physicians)
        ? physicians.map(p => ({ ...p, fullName: `${p.lastName} ${p.firstName}` }))
        : [];

    // Transform schedule items to the format expected by Syncfusion ScheduleComponent
    const eventData = scheduleItems && Array.isArray(scheduleItems)
        ? scheduleItems.map(item => ({
            Id: item.id,
            Subject: item.subject,
            StartTime: new Date(item.startTime),
            EndTime: new Date(item.endTime),
            RoomCode: item.roomId,
            PhysicianId: item.physicianId,
            Physician: item.physician,
            PatientLastName: item.patientLastName,
            PatientFirstName: item.patientFirstName
        }))
        : [];

    const hasRooms = activeRooms && Array.isArray(activeRooms) && activeRooms.length > 0;
    const hasPhysicians = processedPhysicians.length > 0;
    const hasScheduleData = scheduleItems && Array.isArray(scheduleItems);

    // Consolidated loading state
    const isAnyLoading = isLoading || isLoadingPhysicians || isLoadingSchedule;
    
    const navigate = useNavigate();

    const handleCreateEncounter = () => {
        navigate(`/encounter/create?company=${encodeURIComponent(company ?? '')}&center=${encodeURIComponent(center ?? '')}`);
    };



    // Parse schedule item ID to extract encounter and procedure information
    const parseScheduleItemId = (scheduleId: string) => {
        // Format: PSN-H01-13-2_temp-1753320980255
        // Split by underscore to separate encounter ID and procedure ID
        const parts = scheduleId.split('_');
        let encounterId = scheduleId;
        let procedureId = null;
        if (parts.length === 2) {
            encounterId = parts[0];
            procedureId = parts[1];
        }
        // Parse encounterId: <Company>-<Center>-<MRN>-<VisitNumber>
        const idParts = encounterId.split('-');
        const company = idParts[0] || '';
        const center = idParts[1] || '';
        const mrn = idParts[2] || '';
        return { encounterId, procedureId, company, center, mrn };
    };

    // Customize the quick popup's Edit button to redirect (remove patient name injection)
    const onPopupOpen = (args: any) => {
        if (args.type === 'QuickInfo' && args.data && args.data.Id) {
            // Remove any previously injected patient name node if present
            const content = args.element.querySelector('.e-quick-popup-wrapper .e-popup-content') as HTMLElement;
            if (content) {
                const prev = content.querySelector('.custom-patient-name');
                if (prev) prev.remove();
            }
            // Hide the Delete button in the popup
            const deleteBtn = args.element.querySelector('.e-quick-popup-wrapper .e-event-delete') as HTMLElement;
            if (deleteBtn) {
                deleteBtn.style.display = 'none';
            }
            // Find the Edit button in the popup
            const editBtn = args.element.querySelector('.e-quick-popup-wrapper .e-event-edit') as HTMLElement;
            if (editBtn) {
                editBtn.onclick = (e: Event) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // Parse the schedule item ID to get encounter and procedure IDs and other params
                    const { encounterId, procedureId, company, center, mrn } = parseScheduleItemId(args.data.Id);
                    // Build the query string
                    let editUrl = `/encounterEdit?id=${encodeURIComponent(encounterId)}&company=${encodeURIComponent(company)}&center=${encodeURIComponent(center)}&mrn=${encodeURIComponent(mrn)}`;
                    if (procedureId) editUrl += `&procedureId=${encodeURIComponent(procedureId)}`;
                    navigate(editUrl);
                    // Close the popup
                    const closeBtn = args.element.querySelector('.e-quick-popup-wrapper .e-close') as HTMLElement;
                    if (closeBtn) closeBtn.click();
                };
            }
        }
    };

    // Handle double click on event to redirect to edit page
    const onEventDoubleClick = (args: any) => {
        if (args && args.event && (args.event.Id || args.event.id)) {
            const eventId = args.event.Id || args.event.id;
            // Parse the schedule item ID to get encounter and procedure IDs and other params
            const { encounterId, procedureId, company, center, mrn } = parseScheduleItemId(eventId);
            // Build the query string
            let editUrl = `/encounterEdit?id=${encodeURIComponent(encounterId)}&company=${encodeURIComponent(company)}&center=${encodeURIComponent(center)}&mrn=${encodeURIComponent(mrn)}`;
            if (procedureId) editUrl += `&procedureId=${encodeURIComponent(procedureId)}`;
            navigate(editUrl);
            // Prevent default editor from opening
            args.cancel = true;
        }
    };

    // Map event fields for Syncfusion
    const fieldsData = {
        id: 'Id',
        subject: { name: 'Subject' },
        startTime: { name: 'StartTime' },
        endTime: { name: 'EndTime' },
        isAllDay: { name: 'IsAllDay' },
        physician: { name: 'Physician' }, // Map to Physician (full name)
        location: { name: 'Physician' }, // Map location to Physician for event display
        roomCode: { name: 'RoomCode' }, // Resource field for rooms (changed to roomCode)
        // Add more mappings as needed
    };

    return (
        <SectionPaper sx={{ p: 4, mt: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ flex: 1, mr: 2 }}>
                    <CenterSelector value={center} onChange={handleCenterChange} />
                </Box>
                {!isAnyLoading && (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                        <ScheduleExportButton />
                        <SearchPatientsButton company={company} center={center} />
                    </Stack>
                )}
            </Box>
            {isAnyLoading && <Loading />}
            {(isError || isErrorPhysicians || isErrorSchedule) && (
                <Box>
                    <Typography color="error">Failed to load schedule data.</Typography>
                    {isErrorSchedule && scheduleError && (
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            Schedule API Error: {JSON.stringify(scheduleError)}
                        </Typography>
                    )}
                </Box>
            )}
            <div>
                {hasScheduleData && !isAnyLoading ? (
                    <ScheduleComponent
                        height="800px"
                        selectedDate={today}
                        currentView="Day"
                        workHours={{
                            highlight: true,
                            start: '06:00',
                            end: '17:00'
                        }}
                        group={{
                            resources: ['Rooms']
                        }}
                        eventSettings={{
                            dataSource: eventData,
                            fields: fieldsData,
                            allowAdding: false // Disable adding new events
                        }}
                        popupOpen={onPopupOpen}
                        eventDoubleClick={onEventDoubleClick}
                    >
                        <ResourcesDirective>
                            <ResourceDirective
                                field="RoomCode"
                                title="Room"
                                name="Rooms"
                                allowMultiple={false}
                                dataSource={activeRooms || []}
                                textField="description"
                                idField="code"
                                colorField="color"
                            />
                        </ResourcesDirective>
                        <Inject services={[Day, Week, WorkWeek, Month, Agenda]} />
                    </ScheduleComponent>
                ) : (
                    !isAnyLoading && !isError && !isErrorPhysicians && !isErrorSchedule && (
                        <Typography>No schedule data available for this center.</Typography>
                    )
                )}
            </div>
        </SectionPaper>
    );
};

const Schedule: React.FC = () => {
    return (
        <AuthenticatedTemplate>
            <ScheduleContent />
        </AuthenticatedTemplate>
    );
};

export default Schedule;
